import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface ESPNAthleteFilters {
  sport?: string;
  searchTerm?: string;
  claimStatus?: string;
  team?: string;
  conference?: string;
  isCollegiate?: boolean;
  limit?: number;
  offset?: number;
}

export interface ESPNAthlete {
  id: string;
  espn_id: number;
  sport: string;
  league: string | null;
  name: string;
  first_name: string | null;
  last_name: string | null;
  display_name: string | null;
  short_name: string | null;
  age: number | null;
  birth_date: string | null;
  height: string | null;
  weight: string | null;
  birthplace_city: string | null;
  birthplace_state: string | null;
  birthplace_country: string | null;
  college: string | null;
  draft_year: number | null;
  draft_round: number | null;
  draft_number: number | null;
  years_experience: number | null;
  current_team_id: number | null;
  current_team_name: string | null;
  current_team_logo: string | null;
  current_team_abbreviation: string | null;
  position: string | null;
  jersey_number: number | null;
  headshot_url: string | null;
  bio: string | null;
  career_stats: Record<string, any> | null;
  current_season_stats: Record<string, any> | null;
  claim_status: string | null;
  claimed_by_profile_id: string | null;
  claimed_at: string | null;
  is_verified: boolean | null;
  last_synced_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  api_data: Record<string, any> | null;
  // NCAA-specific fields
  conference: string | null;
  division: string | null;
  is_collegiate: boolean | null;
}

export const useESPNAthletesData = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [espnAthleteFilters, setEspnAthleteFilters] = useState<ESPNAthleteFilters>({
    limit: 20,
    offset: 0,
  });

  // Fetch ESPN athlete stats using server-side RPC (bypasses row limits)
  const { data: espnAthleteStats, isLoading: espnStatsLoading } = useQuery({
    queryKey: ['espn-athlete-stats'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_espn_athlete_stats');
      if (error) throw error;
      return data as {
        totalAthletes: number;
        bySport: Record<string, { total: number; claimed: number; pending: number }>;
      };
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Fetch ESPN athletes with filters
  const { 
    data: espnAthletesData, 
    isLoading: espnAthletesLoading,
    refetch: refetchEspnAthletes,
  } = useQuery({
    queryKey: ['espn-athletes', espnAthleteFilters],
    queryFn: async () => {
      let query = supabase
        .from('espn_athletes')
        .select('*', { count: 'exact' });

      // Apply filters
      if (espnAthleteFilters.sport) {
        query = query.ilike('sport', espnAthleteFilters.sport);
      }

      if (espnAthleteFilters.searchTerm) {
        query = query.or(`name.ilike.%${espnAthleteFilters.searchTerm}%,display_name.ilike.%${espnAthleteFilters.searchTerm}%`);
      }

      if (espnAthleteFilters.claimStatus && espnAthleteFilters.claimStatus !== 'all') {
        query = query.eq('claim_status', espnAthleteFilters.claimStatus);
      }

      if (espnAthleteFilters.team && espnAthleteFilters.team !== 'all') {
        query = query.eq('current_team_name', espnAthleteFilters.team);
      }

      if (espnAthleteFilters.conference && espnAthleteFilters.conference !== 'all') {
        query = query.eq('conference', espnAthleteFilters.conference);
      }

      if (espnAthleteFilters.isCollegiate !== undefined) {
        query = query.eq('is_collegiate', espnAthleteFilters.isCollegiate);
      }

      // Apply pagination
      const limit = espnAthleteFilters.limit || 20;
      const offset = espnAthleteFilters.offset || 0;
      query = query.range(offset, offset + limit - 1);

      // Order by name
      query = query.order('name', { ascending: true });

      const { data, count, error } = await query;

      if (error) throw error;

      return {
        athletes: data as ESPNAthlete[],
        total: count || 0,
      };
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
  });

  // Fetch unique teams for ESPN athletes
  const { data: espnUniqueTeams } = useQuery({
    queryKey: ['espn-unique-teams', espnAthleteFilters.sport],
    queryFn: async () => {
      let query = supabase
        .from('espn_athletes')
        .select('current_team_name');

      if (espnAthleteFilters.sport) {
        query = query.ilike('sport', espnAthleteFilters.sport);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Get unique team names
      const teams = new Set<string>();
      for (const athlete of data || []) {
        if (athlete.current_team_name) {
          teams.add(athlete.current_team_name);
        }
      }
      return Array.from(teams).sort();
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Fetch unique conferences for NCAA athletes
  const { data: espnUniqueConferences } = useQuery({
    queryKey: ['espn-unique-conferences', espnAthleteFilters.sport],
    queryFn: async () => {
      let query = supabase
        .from('espn_athletes')
        .select('conference')
        .eq('is_collegiate', true);

      if (espnAthleteFilters.sport) {
        query = query.ilike('sport', espnAthleteFilters.sport);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Get unique conference names
      const conferences = new Set<string>();
      for (const athlete of data || []) {
        if (athlete.conference) {
          conferences.add(athlete.conference);
        }
      }
      return Array.from(conferences).sort();
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Fetch ALL filtered ESPN athlete IDs (for Select All functionality)
  const fetchAllFilteredEspnAthleteIds = async (): Promise<string[]> => {
    let query = supabase
      .from('espn_athletes')
      .select('id');

    // Apply same filters as main query (without pagination)
    if (espnAthleteFilters.sport) {
      query = query.ilike('sport', espnAthleteFilters.sport);
    }

    if (espnAthleteFilters.searchTerm) {
      query = query.or(`name.ilike.%${espnAthleteFilters.searchTerm}%,display_name.ilike.%${espnAthleteFilters.searchTerm}%`);
    }

    if (espnAthleteFilters.claimStatus && espnAthleteFilters.claimStatus !== 'all') {
      query = query.eq('claim_status', espnAthleteFilters.claimStatus);
    }

    if (espnAthleteFilters.team && espnAthleteFilters.team !== 'all') {
      query = query.eq('current_team_name', espnAthleteFilters.team);
    }

    if (espnAthleteFilters.conference && espnAthleteFilters.conference !== 'all') {
      query = query.eq('conference', espnAthleteFilters.conference);
    }

    if (espnAthleteFilters.isCollegiate !== undefined) {
      query = query.eq('is_collegiate', espnAthleteFilters.isCollegiate);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data?.map(a => a.id) || [];
  };

  // Delete single ESPN athlete
  const deleteEspnAthleteMutation = useMutation({
    mutationFn: async (athleteId: string) => {
      const { error } = await supabase
        .from('espn_athletes')
        .delete()
        .eq('id', athleteId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: 'Athlete Deleted',
        description: 'The ESPN athlete has been removed from the database.',
      });
      queryClient.invalidateQueries({ queryKey: ['espn-athletes'] });
      queryClient.invalidateQueries({ queryKey: ['espn-athlete-stats'] });
      queryClient.invalidateQueries({ queryKey: ['espn-unique-teams'] });
    },
    onError: (error) => {
      toast({
        title: 'Delete Failed',
        description: error instanceof Error ? error.message : 'Failed to delete athlete',
        variant: 'destructive',
      });
    },
  });

  // Bulk delete ESPN athletes
  const deleteEspnAthletesMutation = useMutation({
    mutationFn: async (athleteIds: string[]) => {
      const { error } = await supabase
        .from('espn_athletes')
        .delete()
        .in('id', athleteIds);
      
      if (error) throw error;
    },
    onSuccess: (_, athleteIds) => {
      toast({
        title: 'Athletes Deleted',
        description: `Successfully deleted ${athleteIds.length} ESPN athletes.`,
      });
      queryClient.invalidateQueries({ queryKey: ['espn-athletes'] });
      queryClient.invalidateQueries({ queryKey: ['espn-athlete-stats'] });
      queryClient.invalidateQueries({ queryKey: ['espn-unique-teams'] });
    },
    onError: (error) => {
      toast({
        title: 'Delete Failed',
        description: error instanceof Error ? error.message : 'Failed to delete athletes',
        variant: 'destructive',
      });
    },
  });

  // Delete ALL ESPN athletes by sport (or all if no sport specified)
  const deleteAllEspnAthletesBySportMutation = useMutation({
    mutationFn: async (sport?: string) => {
      let query = supabase
        .from('espn_athletes')
        .delete();
      
      if (sport) {
        query = query.ilike('sport', sport);
      } else {
        // Delete all - need a condition, so use a truthy condition
        query = query.gte('created_at', '1970-01-01');
      }
      
      const { error } = await query;
      if (error) throw error;
    },
    onSuccess: (_, sport) => {
      toast({
        title: 'Athletes Deleted',
        description: `Successfully deleted all ${sport ? sport.toUpperCase() : ''} ESPN athletes.`,
      });
      queryClient.invalidateQueries({ queryKey: ['espn-athletes'] });
      queryClient.invalidateQueries({ queryKey: ['espn-athlete-stats'] });
      queryClient.invalidateQueries({ queryKey: ['espn-unique-teams'] });
    },
    onError: (error) => {
      toast({
        title: 'Delete Failed',
        description: error instanceof Error ? error.message : 'Failed to delete athletes',
        variant: 'destructive',
      });
    },
  });

  return {
    // Stats
    espnAthleteStats,
    espnStatsLoading,
    
    // Athletes list
    espnAthletes: espnAthletesData?.athletes || [],
    espnAthletesTotal: espnAthletesData?.total || 0,
    espnAthletesLoading,
    refetchEspnAthletes,
    
    // Filters
    espnAthleteFilters,
    setEspnAthleteFilters,
    
    // Unique values for dropdowns
    espnUniqueTeams: espnUniqueTeams || [],
    espnUniqueConferences: espnUniqueConferences || [],
    
    // Fetch all filtered IDs (for Select All)
    fetchAllFilteredEspnAthleteIds,
    
    // Mutations
    deleteEspnAthlete: deleteEspnAthleteMutation.mutate,
    deletingEspnAthlete: deleteEspnAthleteMutation.isPending,
    deleteEspnAthletes: deleteEspnAthletesMutation.mutate,
    deletingEspnAthletes: deleteEspnAthletesMutation.isPending,
    
    // Delete all by sport
    deleteAllEspnAthletesBySport: deleteAllEspnAthletesBySportMutation.mutate,
    deletingAllEspnAthletes: deleteAllEspnAthletesBySportMutation.isPending,
  };
};
